import { Injectable } from '@angular/core';
import { AssetTypeMapperService } from '@angular-workspace/core';

@Injectable({
  providedIn: 'root'
})
export class HelperService {

  constructor(private assetTypeMapper: AssetTypeMapperService) { }

  /**
   * Map display asset type to API format
   * @param displayType Display name (e.g., "Dataset", "Model", "App", "Use Case")
   * @returns API format (e.g., "adex:DataBank", "adex:AiModel", "adex:Apps", "adex:UseCase")
   */
  mapAssetTypeToApiFormat(displayType: string): string {
    return this.assetTypeMapper.mapAssetTypeToApiFormat(displayType);
  }

  /**
   * Scroll to an element by ID
   * @param elementId The ID of the element to scroll to
   * @param behavior Scroll behavior ('smooth' | 'auto')
   * @param block Vertical alignment ('start' | 'center' | 'end' | 'nearest')
   */
  scrollToElement(
    elementId: string, 
    behavior: ScrollBehavior = 'smooth', 
    block: ScrollLogicalPosition = 'start'
  ): void {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior, block });
    }
  }

  /**
   * Check if a value is null or undefined
   * @param value The value to check
   * @returns True if the value is null or undefined
   */
  isNullOrUndefined(value: unknown): value is null | undefined {
    return value === null || value === undefined;
  }

  /**
   * Check if a string is empty or whitespace
   * @param value The string to check
   * @returns True if the string is empty or only whitespace
   */
  isEmptyString(value: string | null | undefined): boolean {
    return !value || value.trim().length === 0;
  }

  /**
   * Deep clone an object
   * @param obj The object to clone
   * @returns A deep copy of the object
   */
  deepClone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }

  /**
   * Generate a unique ID
   * @param prefix Optional prefix for the ID
   * @returns A unique string ID
   */
  generateUniqueId(prefix: string = 'id'): string {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Debounce a function
   * @param func The function to debounce
   * @param delay Delay in milliseconds
   * @returns Debounced function
   */
  debounce<T extends (...args: any[]) => any>(
    func: T,
    delay: number
  ): (...args: Parameters<T>) => void {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }

  /**
   * Extract and map access policy/status to display badge text
   * Maps: "Restricted" → "Restricted", "Open" → "Public"
   * @param item API item with accessPolicy, publishStatus, or status field
   * @returns Formatted badge text
   */
  extractAccessBadge(item: any): string {
    // Check accessPolicy first
    if (item.accessPolicy) {
      const policy = item.accessPolicy.toLowerCase();
      if (policy === 'restricted') {
        return 'Restricted';
      } else if (policy === 'open') {
        return 'Public';
      }
      // For any other accessPolicy value, capitalize first letter
      return item.accessPolicy.charAt(0).toUpperCase() + item.accessPolicy.slice(1).toLowerCase();
    }
    
    // Fallback to publishStatus
    if (item.publishStatus) {
      return item.publishStatus.charAt(0).toUpperCase() + item.publishStatus.slice(1).toLowerCase();
    }
    
    // Fallback to status
    if (item.status) {
      return item.status.charAt(0).toUpperCase() + item.status.slice(1).toLowerCase();
    }
    
    // Default to Public
    return 'Public';
  }

  /**
   * Get badge color variant based on access level
   * @param badge Badge text (e.g., "Public", "Restricted", "Private")
   * @returns Badge color variant
   */
  getBadgeColorVariant(badge: string): 'public' | 'restricted' | 'private' {
    const lowerBadge = badge.toLowerCase();
    if (lowerBadge === 'restricted') return 'restricted';
    if (lowerBadge === 'private') return 'private';
    return 'public';
  }
}

